/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, post, type ApiResult } from "./client";

/**
 * Staging demo panel data source. The backend registers these routes only outside prod, so the
 * capability probe failing means the whole surface stays hidden.
 */

export type DemoRecipient = { label: string; phone: string };

export type DemoOnboardingScenario = {
  id: string;
  title: string;
  description: string;
  walk: string[];
  watch: string;
  runnable: boolean;
  /** Only on a case with a run of its own. */
  stateCode?: string;
  appointmentKind?: "INTAKE" | "CONSULT";
  insuranceHint?: string | null;
};

/** A queued text waits until `scheduledAt`, in `zone`. UNKNOWN: nothing can say whether it went. */
export type DemoInvite = {
  sms: "SENT" | "QUEUED" | "SKIPPED" | "UNKNOWN";
  scheduledAt: string | null;
  zone: string | null;
  reason: string | null;
};

export type DemoOnboardingRun = {
  onboardingId: string;
  referralId: number;
  familyLink: string;
  childFirstName: string;
  childDob: string;
  caregiverEmail: string;
  invite: DemoInvite;
};

export type DemoCapability = {
  enabled: boolean;
  providerHealthieId: string;
  bookablePatientHealthieId: string;
  caregiverMailbox: string;
  recipients: DemoRecipient[];
  onboardingScenarios: DemoOnboardingScenario[];
};

export type DemoResetResult = {
  touched: number;
  onboardingsEnded: number;
  onboardingsFailed: number;
};

export type FreedSlotResult = {
  appointmentHealthieId: string;
  freedSlotStartsAt: string;
};

export function getDemoCapability(): Promise<ApiResult<DemoCapability>> {
  return api<DemoCapability>("/v1/admin/demo");
}

export function runFreedSlot(
  toPhone: string,
  secondToPhone?: string,
): Promise<ApiResult<FreedSlotResult>> {
  return api<FreedSlotResult>(
    "/v1/admin/demo/freed-slot",
    post(secondToPhone ? { toPhone, secondToPhone } : { toPhone }),
  );
}

export function sendReply(toPhone: string, body: string): Promise<ApiResult<{ accepted: boolean }>> {
  return api<{ accepted: boolean }>("/v1/admin/demo/reply", post({ toPhone, body }));
}

export function seedOffer(
  toPhone: string,
  options: { patientHealthieId?: string; rank?: number; score?: string } = {},
): Promise<ApiResult<{ outreachId: string }>> {
  return api<{ outreachId: string }>("/v1/admin/demo/seed-offer", post({ toPhone, ...options }));
}

export function resetDemo(toPhone: string): Promise<ApiResult<DemoResetResult>> {
  return api<DemoResetResult>("/v1/admin/demo/reset", post({ toPhone }));
}

export function runOnboardingScenario(
  scenario: string,
  toPhone: string,
): Promise<ApiResult<DemoOnboardingRun>> {
  return api<DemoOnboardingRun>("/v1/admin/demo/onboarding", post({ scenario, toPhone }));
}

export function addRecipient(label: string, phone: string): Promise<ApiResult<DemoCapability>> {
  return api<DemoCapability>("/v1/admin/demo/recipients", post({ label, phone }));
}

export function updateRecipient(
  phone: string,
  label: string,
  newPhone: string,
): Promise<ApiResult<DemoCapability>> {
  return api<DemoCapability>("/v1/admin/demo/recipients/update", post({ phone, label, newPhone }));
}

export function removeRecipient(phone: string): Promise<ApiResult<DemoCapability>> {
  return api<DemoCapability>("/v1/admin/demo/recipients/remove", post({ phone }));
}

export function advanceTimers(): Promise<ApiResult<{ aged: number }>> {
  return api<{ aged: number }>("/v1/admin/demo/advance-timers", post({}));
}

export function seedTransition(): Promise<ApiResult<{ touched: number }>> {
  return api<{ touched: number }>("/v1/admin/demo/seed-transition", post({}));
}
